import { useDisclosure } from "@mantine/hooks";
import { Suspense, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { colors } from "#app/theme/v1/colors";
import { usePart } from "#modules/parts/view/usePart";
import {
  getDefaultValues,
  isAddedValueProcessStep,
} from "#modules/quoteRequestLines/getDefaultValues";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { useSurfaceTreatments } from "#modules/quoteRequestLines/view/useSurfaceTreatments";
import { AlertChip } from "#modules/quoteRequestLines/views/AlertChip";
import { DetailsPanel } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanel";
import { QuoteRequestLineContent } from "#modules/quoteRequestLines/views/QuoteRequestLine/QuoteRequestLineContent";
import { QuoteRequestLineSkeleton } from "#modules/quoteRequestLines/views/QuoteRequestLine/QuoteRequestLineSkeleton";
import { ReliableChip } from "#modules/quoteRequestLines/views/ReliableChip";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Alert } from "#shared/view/components/Alert";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Checkbox } from "#shared/view/designSystem/Checkbox";
import { Collapse } from "#shared/view/designSystem/Collapse";
import { AssemblyWithChildren, JobStatusSchema, PartTypeSchema } from "shared";

import { getCanDisplayReliableChip } from "./domain/getCanDisplayReliableChip";
import { sortQuoteRequestLineByPartType } from "./domain/sortQuoteRequestLineByPartType";
import { getIsAnalyzingPart } from "./helpers/getIsAnalyzingPart";
import { useQuoteRequestLineCustomInputs } from "./view/useQuoteRequestLineCustomInputs";
import { QuoteRequestLineContainer } from "./views/QuoteRequestLine/QuoteRequestLineContainer";
import { useCustomInputs } from "./views/useCustomInputs";
import { useShouldShowCustomInputs } from "./views/useShouldShowCustomInputs";

const ANALYZING_PART_POLL_MS = 10000;

export const QuoteRequestLine = ({
  quoteRequestLineId,
  isParent,
  assemblyChildren,
  assembliesInBatch,
  isCustomInputsBulkLoading,
}: {
  quoteRequestLineId: string;
  isParent: boolean;
  parentQuoteRequestLineId?: string;
  assemblyChildren: AssemblyWithChildren<QuoteRequestLineDto>[];
  assembliesInBatch?: AssemblyWithChildren<QuoteRequestLineDto>[];
  isCustomInputsBulkLoading: boolean;
}) => {
  const isSingle = isParent && assemblyChildren.length === 0;
  const isAssemblyParent = isParent && !isSingle;
  const isChild = assemblyChildren.length === 0 && !isParent;

  const [isAssemblyOpen, { toggle: toggleAssembly }] = useDisclosure(false);

  const { selectedQuoteRequestLines, setSelectedQuoteRequestLines } = useAssembliesContext();

  const [activePanelId, setActivePanelId] = useState<string | null>(null);

  const sidePanelOpen = useCallback((id: string) => setActivePanelId(id), []);
  const sidePanelClose = useCallback(() => setActivePanelId(null), []);

  const {
    quoteRequestLine,
    isFetching: isFetchingQuoteRequestLine,
    isFetched: isFetchedQuoteRequestLine,
  } = useQuoteRequestLine({ id: quoteRequestLineId });

  const { part } = usePart(quoteRequestLine?.partId, {
    enabled: !!quoteRequestLine?.partId,
    initialData: quoteRequestLine?.part ?? undefined,
    refetchInterval: (query) =>
      query.state.data && getIsAnalyzingPart(query.state.data) ? ANALYZING_PART_POLL_MS : false,
  });

  const shouldShowCustomInputs = useShouldShowCustomInputs(part?.type);

  const { customInputs: customInputsValues } = useQuoteRequestLineCustomInputs(quoteRequestLineId, {
    enabled: shouldShowCustomInputs && !isCustomInputsBulkLoading,
  });
  const { customInputs } = useCustomInputs({ enabled: shouldShowCustomInputs });

  const predictionStatus = quoteRequestLine?.predictionStatus;

  const isDetailsPanelDisplayable =
    ((predictionStatus === JobStatusSchema.enum.PROCESSED ||
      predictionStatus === JobStatusSchema.enum.ERROR) &&
      isFetchedQuoteRequestLine &&
      part?.type !== PartTypeSchema.enum.CONSUMABLE) ||
    isAssemblyParent;

  const canDisplayReliableChip = getCanDisplayReliableChip({
    isFetchedQuoteRequestLine,
    isAssemblyParent,
    part,
  });
  const isAlertChipDisplayable =
    (predictionStatus === JobStatusSchema.enum.PROCESSED ||
      predictionStatus === JobStatusSchema.enum.ERROR) &&
    isFetchedQuoteRequestLine &&
    part?.type !== PartTypeSchema.enum.CONSUMABLE;

  const { surfaceTreatments } = useSurfaceTreatments();

  const defaultValues = {
    ...getDefaultValues({
      quoteRequestLine,
      customInputsValues,
      customInputs,
      surfaceTreatments,
    }),
    isDetailsPanelDisplayable,
  };
  const processSteps = quoteRequestLine?.processSteps.filter(isAddedValueProcessStep) ?? [];

  const formMethods = useForm<QuoteRequestLineFormData>({
    values: defaultValues,
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const shouldShowSkeleton = isFetchingQuoteRequestLine && !isFetchedQuoteRequestLine;

  const { reset } = formMethods;

  const isSidePanelOpen = activePanelId === quoteRequestLineId;

  const onCloseSidePanel = () => {
    sidePanelClose();
    reset();
  };

  const openDetailsPanelForQuoteRequestLine = (targetQuoteRequestLineId: string) => {
    if (isDetailsPanelDisplayable) {
      sidePanelOpen(targetQuoteRequestLineId);
    }
  };

  const sortedChildren = [...assemblyChildren].sort(sortQuoteRequestLineByPartType);

  if (shouldShowSkeleton) {
    return (
      <Stack gap="xxs" w={"100%"}>
        <QuoteRequestLineContainer
          quoteRequestLineId={quoteRequestLine?.id}
          isReliable={quoteRequestLine?.isReliable}
          backgroundColor={isParent && !isSingle ? colors.gray[1] : ""}
          isDetailsPanelDisplayable={isDetailsPanelDisplayable}
        >
          <QuoteRequestLineSkeleton />
        </QuoteRequestLineContainer>
      </Stack>
    );
  }

  if (!quoteRequestLine) {
    return (
      <Stack gap="xxs" w={"100%"}>
        <Alert title="Une erreur réseau est survenue" color="red">
          Demande de devis non trouvée
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack
      gap="xxs"
      w={"100%"}
      pos="relative"
      onClick={(e) => {
        e.stopPropagation();
        openDetailsPanelForQuoteRequestLine(quoteRequestLine.id);
      }}
    >
      <FormProvider {...formMethods}>
        <QuoteRequestLineContainer
          quoteRequestLineId={quoteRequestLine.id}
          isReliable={quoteRequestLine.isReliable}
          backgroundColor={isParent && !isSingle ? colors.gray[1] : ""}
          isDetailsPanelDisplayable={isDetailsPanelDisplayable}
        >
          <Flex justify={"space-between"} align={"center"} w={"100%"} h={"100%"}>
            <Suspense fallback={<QuoteRequestLineSkeleton />}>
              <QuoteRequestLineContent
                hasChildren={isAssemblyParent}
                isChild={isChild}
                openDetailsPanelForQuoteRequestLine={openDetailsPanelForQuoteRequestLine}
                quoteRequestLine={quoteRequestLine}
                part={part}
                toggleAssembly={toggleAssembly}
                isAssemblyOpen={isAssemblyOpen}
                assembliesInBatch={assembliesInBatch ?? []}
                isDetailsPanelDisplayable={isDetailsPanelDisplayable}
                polledPredictionStatus={predictionStatus}
              />
            </Suspense>
          </Flex>
        </QuoteRequestLineContainer>
        <Collapse expanded={isAssemblyOpen}>
          <Stack gap="xxs">
            {sortedChildren.map((child) => {
              const childQuoteRequestLine = child.quoteRequestLine;
              const childId = childQuoteRequestLine.id;
              const isChildSelected = selectedQuoteRequestLines.some((qrl) => qrl.id === childId);
              const childInBatch = assembliesInBatch
                ?.map(
                  (assembly) =>
                    assembly.children.find(
                      (c) => c.quoteRequestLine.part.id === child.quoteRequestLine.part.id,
                    )?.quoteRequestLine,
                )
                .filter((c) => c !== undefined) ?? [childQuoteRequestLine];
              const childInBatchIds = childInBatch.map((c) => c.id);

              const onToggle = () =>
                setSelectedQuoteRequestLines((prev) => {
                  const allSelected = childInBatchIds.every((id) =>
                    prev.some((qrl) => qrl.id === id),
                  );

                  return allSelected
                    ? prev.filter((qrl) => !childInBatchIds.some((id) => qrl.id === id))
                    : [
                        ...prev,
                        ...childInBatch.filter((qrl) => !prev.some((qrl2) => qrl2.id === qrl.id)),
                      ];
                });

              return (
                <Group
                  key={childId}
                  align="center"
                  wrap="nowrap"
                  gap="xxs"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Stack gap="xs" pt="xs" align="center">
                    <Checkbox
                      size="xs"
                      color={colors.blue[4]}
                      checked={isChildSelected}
                      onChange={onToggle}
                      onClick={(e) => e.stopPropagation()}
                      styles={{ input: { cursor: "pointer" } }}
                    />
                  </Stack>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <QuoteRequestLine
                      quoteRequestLineId={childId}
                      isParent={false}
                      assemblyChildren={[]}
                      isCustomInputsBulkLoading={isCustomInputsBulkLoading}
                    />
                  </Box>
                </Group>
              );
            })}
          </Stack>
        </Collapse>

        <DetailsPanel
          onClosePanel={onCloseSidePanel}
          sidePanelOpen={isSidePanelOpen}
          part={part}
          quoteRequestLine={quoteRequestLine}
          assembliesInBatch={assembliesInBatch ?? []}
          assemblyChildren={assemblyChildren}
          onAssemblyActivatedForBatch={sidePanelOpen}
          hasChildren={isAssemblyParent}
          isChild={isChild}
          processSteps={processSteps}
        />
      </FormProvider>

      <Box
        pos="absolute"
        style={{ transform: "translateY(-33%)" }}
        top={0}
        right="var(--mantine-spacing-xs)"
      >
        <Flex gap="xxs" align="center" onClick={(e) => e.stopPropagation()}>
          {isAlertChipDisplayable && (
            <AlertChip quoteRequestLine={quoteRequestLine} assemblyChildren={assemblyChildren} />
          )}
          {canDisplayReliableChip && <ReliableChip quoteRequestLine={quoteRequestLine} />}
        </Flex>
      </Box>
    </Stack>
  );
};
